import DownloadIcon from '@mui/icons-material/Download'
import LaunchIcon from '@mui/icons-material/Launch'
import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader } from '@mui/material'
import { useNewsletterSubscriptionsExport } from '@src/data/api/report/newsletter-subscription-api'
import { useReportActExport } from '@src/data/api/report/reportAct-api'
import { useReportAllApplicationExport } from '@src/data/api/report/reportAllApplication-api'
import { useReportBenoitExport } from '@src/data/api/report/reportBenoit-api'
import { useReportNeoExport } from '@src/data/api/report/reportNeo-api'
import { useReportNewsLetterExport } from '@src/data/api/report/reportNewsLetter-api'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'

const Reports = (): JSX.Element => {
  const { t } = useTranslation()

  const user = useUser()
  const [exportNewsletter, isExportingNewsletter] = useNewsletterSubscriptionsExport()
  const [exportAllApplication, isExportingAllApplication] = useReportAllApplicationExport()
  const [exportBenoit, isExportingBenoit] = useReportBenoitExport()
  const [exportAct, isExportingAct] = useReportActExport()
  const [exportNeo, isExportingNeo] = useReportNeoExport()
  const [exportNewsLetter, isExportingNewsLetter] = useReportNewsLetterExport()

  const handleAllCreditApplicationsReportClick = async () => {
    try {
      await exportAllApplication()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleReportBenoit = async () => {
    try {
      await exportBenoit()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleReportAct = async () => {
    try {
      await exportAct()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleReportNeo = async () => {
    try {
      await exportNeo()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleNewsletterExport = async () => {
    try {
      await exportNewsletter()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  const handleReportNewsLetter = async () => {
    try {
      await exportNewsLetter()
    } catch (error) {
      reportErrorToConsole(error)
    }
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <List
        sx={{ minWidth: 350, maxWidth: '100%' }}
        subheader={
          <ListSubheader component="div" id="nested-list-subheader">
            {t('report.allReport')}
          </ListSubheader>
        }
      >
        <ListItemButton href="/reports/payoutThatSkippedIncomeValidation">
          <ListItemText primary={t('report.payoutWithoutFlinks.title')} />
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <LaunchIcon />
          </ListItemIcon>
        </ListItemButton>
        <Divider />

        {user?.rights.canDownloadAllApplicationsReport && (
          <>
            <ListItemButton onClick={handleAllCreditApplicationsReportClick} disabled={isExportingAllApplication}>
              <ListItemText
                primary={t('report.reportAllApplication')}
                secondary={isExportingAllApplication ? t('report.download') : ''}
              />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canDownloadBenoitReport && (
          <>
            <ListItemButton onClick={handleReportBenoit} disabled={isExportingBenoit}>
              <ListItemText
                primary={t('report.reportBenoit')}
                secondary={isExportingBenoit ? t('report.download') : ''}
              />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canDownloadActReport && (
          <>
            <ListItemButton onClick={handleReportAct} disabled={isExportingAct}>
              <ListItemText primary={t('report.reportAct')} secondary={isExportingAct ? t('report.download') : ''} />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canDownloadAllApplicationsReport && (
          <>
            <ListItemButton onClick={handleNewsletterExport} disabled={isExportingNewsletter}>
              <ListItemText
                primary={t('report.newsletterSubscriptions')}
                secondary={isExportingNewsletter ? t('report.download') : ''}
              />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canDownloadNeoReport && (
          <>
            <ListItemButton onClick={handleReportNeo} disabled={isExportingNeo}>
              <ListItemText primary={t('report.reportNeo')} secondary={isExportingNeo ? t('report.download') : ''} />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canDownloadNewsLetterReport && (
          <>
            <ListItemButton onClick={handleReportNewsLetter} disabled={isExportingNewsLetter}>
              <ListItemText
                primary={t('report.reportNewsLetter')}
                secondary={isExportingNewsLetter ? t('report.download') : ''}
              />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <DownloadIcon />
              </ListItemIcon>
            </ListItemButton>
            <Divider />
          </>
        )}

        {user?.rights.canReadManagementReports && (
          <ListItemButton href="/reports/agentLog">
            <ListItemText primary={t('report.agentLog.title')} />
            <ListItemIcon sx={{ justifyContent: 'end' }}>
              <LaunchIcon />
            </ListItemIcon>
          </ListItemButton>
        )}

        {user?.rights.canDownloadDocuments && (
          <>
            <Divider />
            <ListItemButton href="/reports/bulk-document-export">
              <ListItemText primary={t('bulkDocumentExport.title')} />
              <ListItemIcon sx={{ justifyContent: 'end' }}>
                <LaunchIcon />
              </ListItemIcon>
            </ListItemButton>
          </>
        )}
      </List>
    </Box>
  )
}

export default Reports
